/**
 * EntityLink — renders a foreign-key value as a clickable link to that entity's
 * view page. Standard, app-wide behavior for every FK on every screen:
 *   - has read permission  -> navigate to the entity's view.
 *   - no read permission   -> show a standard "you don't have access" dialog and
 *                             stay on the current page.
 * Falls back to plain text only when there is nothing to link to (no id / no label).
 *
 *   <EntityLink type="department" id={unit.departmentId} label={unit.department?.name} />
 */
import { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'sharedUi/Button';
import { ENTITY_LINKS, ENTITY_LABEL, entityViewPath, type EntityType } from '../../constants/entity-links';
import { useCanRead } from '../../hooks/usePermissions';
import { useAppNavigate } from '../../hooks/useAppNavigate';

interface EntityLinkProps {
  /** Which entity the id points at (drives the route + permission). */
  type: EntityType;
  /** The referenced entity's id. */
  id?: string | null;
  /** Human-readable label to display (usually the referenced entity's name). */
  label?: string | null;
  /** Shown when there is nothing to link to. */
  fallback?: string;
  className?: string;
  testId?: string;
}

const EntityLink = ({ type, id, label, fallback = '-', className = '', testId }: EntityLinkProps) => {
  const canRead = useCanRead(ENTITY_LINKS[type].jobName);
  const navigate = useAppNavigate();
  const [denied, setDenied] = useState(false);

  const text = label || fallback;

  // Nothing to link to → plain text.
  if (!id || !label) {
    return (
      <span className={`text-sm text-ink ${className}`} data-testid={testId}>
        {text}
      </span>
    );
  }

  const handleClick = () => {
    if (canRead) navigate(entityViewPath(type, id));
    else setDenied(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300 ${className}`}
        title={canRead ? `View ${text}` : `Restricted — you don't have access to this ${ENTITY_LABEL[type]}`}
        data-testid={testId}
      >
        <span>{text}</span>
        <i className={`pi ${canRead ? 'pi-external-link' : 'pi-lock'}`} style={{ fontSize: '0.7rem' }} />
      </button>

      <Dialog
        visible={denied}
        onHide={() => setDenied(false)}
        header="Access restricted"
        dismissableMask
        style={{ width: '400px', maxWidth: '92vw' }}
        footer={
          <div className="flex justify-end">
            <Button label="OK" size="small" onClick={() => setDenied(false)} testId="EntityLink.AccessDenied.OK" />
          </div>
        }
        data-testid="EntityLink.AccessDenied"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-amber-50 text-amber-500 dark:bg-amber-500/10">
            <i className="pi pi-lock text-xl" />
          </span>
          <div>
            <p className="m-0 font-medium text-ink">You don't have access</p>
            <p className="m-0 mt-1 text-sm text-ink-muted">
              You don't have permission to view this {ENTITY_LABEL[type]}. Please contact an administrator if you need access.
            </p>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default EntityLink;
